import { getCollection } from "@/api/inventory/getConfig";
import { useUploadNFTImagesMutation } from "@/api/inventory/images";
import {
	Anchor,
	Group,
	List,
	Stack,
	Text,
	rem,
	useMantineTheme,
} from "@mantine/core";
import { Dropzone, type FileWithPath } from "@mantine/dropzone";
import { IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/_auth/$collection/file-upload")({
	loader: async ({ params, context }) => {
		const collection = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: params.collection,
		});
		return collection;
	},
	component: FileUploadComponent,
});

function FileUploadComponent() {
	const collection = Route.useLoaderData();
	const theme = useMantineTheme();
	const uploadImages = useUploadNFTImagesMutation();

	const handleImageDrop = async (files: FileWithPath[]) => {
		await uploadImages.mutateAsync({
			files,
			collectionAddress: collection.address,
		});
	};

	return (
		<Stack gap="md" p="md" maw={900} mx="auto">
			<Text size="xl" fw={700}>
				Upload Files
			</Text>

			<Stack gap="md">
				<Dropzone
					onDrop={handleImageDrop}
					onReject={() => {
						toast.error("Invalid files", {
							description: "Please upload only PNG files",
						});
					}}
					maxSize={10 * 1024 ** 2} // 10MB
					accept={["image/png"]}
					loading={uploadImages.isPending}
				>
					<Group
						justify="center"
						gap="xl"
						style={{ minHeight: rem(220), pointerEvents: "none" }}
					>
						<Dropzone.Accept>
							<IconUpload
								size="3.2rem"
								stroke={1.5}
								color={theme.colors[theme.primaryColor][4]}
							/>
						</Dropzone.Accept>
						<Dropzone.Reject>
							<IconX size="3.2rem" stroke={1.5} color={theme.colors.red[4]} />
						</Dropzone.Reject>
						<Dropzone.Idle>
							<IconPhoto size="3.2rem" stroke={1.5} />
						</Dropzone.Idle>

						<div>
							<Text size="xl" inline>
								Drag images here or click to select files
							</Text>
							<Text size="sm" inline mt={7}>
								Upload only PNG files.
							</Text>
							<Text size="sm" inline mt={7}>
								File names must be token IDs (e.g., "1.png", "42.png")
							</Text>
							<Text size="sm" inline mt={5}>
								Each file should not exceed 10MB
							</Text>
						</div>
					</Group>
				</Dropzone>

				{uploadImages.isSuccess && uploadImages.data?.files && (
					<List spacing="xs" withPadding>
						{uploadImages.data.files.map((file, index) => {
							const tokenId = file.url
								.split("/")
								.pop()
								?.replace(/\.(png|jpe?g)$/, "");
							return (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<List.Item key={index}>
									<Group justify="space-between" wrap="nowrap">
										<Text>Token ID: {tokenId}</Text>
										<Anchor href={file.url} target="_blank" size="sm">
											{file.url}
										</Anchor>
									</Group>
								</List.Item>
							);
						})}
					</List>
				)}
			</Stack>
		</Stack>
	);
}
